import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, X, Check, Waypoints, Loader2 } from 'lucide-react'
import { vpnLinksApi } from '../../../../services/vpnLinksApi'
import { isVpnHandshakeFresh } from '../../../../utils/vpn'

const ROLES = ['A', 'B', 'C']

/**
 * Multi-link: túneis VPN auxiliares por link WAN (A/B/C) do MikroTik.
 * Cada link vira uma interface automais-vpn-<role> no roteador (aplicada no próximo
 * bootstrap) com IP VPN próprio. O handshake/ping por túnel é telemetria POR LINK:
 * túnel A morto + túnel B vivo = perna A caída, sem investigação.
 * O peer primário (VpnPeerId do device) não aparece aqui — segue no cartão principal.
 */
export default function VpnLinksSection({ device }) {
  const { t } = useTranslation()

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ linkRole: 'A', label: '', mikrotikGateway: '' })
  const [error, setError] = useState(null)

  const load = useCallback(async (silent = false) => {
    if (!device?.id) return
    if (!silent) setLoading(true)
    try {
      const data = await vpnLinksApi.getByDevice(device.id)
      setItems(data || [])
    } catch (e) {
      console.error('vpnLinks load:', e)
      if (!silent) setItems([])
    } finally {
      if (!silent) setLoading(false)
    }
  }, [device?.id])

  useEffect(() => { load() }, [load])

  // Refresh periódico silencioso: handshake/ping dos túneis mudam a cada ciclo do monitor (~1min).
  const pollTimerRef = useRef(null)
  useEffect(() => {
    if (!items.length || !device?.id) return undefined
    pollTimerRef.current = setTimeout(() => load(true), 60000)
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current) }
  }, [items, device?.id, load])

  const usedRoles = new Set(items.map((l) => l.linkRole))
  const freeRoles = ROLES.filter((r) => !usedRoles.has(r))

  const handleAdd = async () => {
    setError(null)
    try {
      await vpnLinksApi.create(device.id, {
        linkRole: form.linkRole,
        label: form.label.trim() || null,
        mikrotikGateway: form.mikrotikGateway.trim() || null,
      })
      setForm({ linkRole: 'A', label: '', mikrotikGateway: '' })
      setAdding(false)
      await load()
    } catch (e) {
      setError(e.response?.data?.message || t('config.vpnLinksAddError', 'Falha ao criar túnel do link'))
    }
  }

  const handleDelete = async (link) => {
    try {
      await vpnLinksApi.delete(device.id, link.id)
      await load()
    } catch (e) {
      alert(e.response?.data?.message || t('config.vpnLinksDeleteError', 'Falha ao remover túnel do link'))
    }
  }

  const handleToggle = async (link) => {
    const newValue = !link.enabled
    setItems((curr) => curr.map((it) => (it.id === link.id ? { ...it, enabled: newValue } : it)))
    try {
      await vpnLinksApi.update(device.id, link.id, { enabled: newValue })
    } catch (e) {
      setItems((curr) => curr.map((it) => (it.id === link.id ? { ...it, enabled: !newValue } : it)))
      alert(e.response?.data?.message || t('config.vpnLinksUpdateError', 'Falha ao atualizar túnel do link'))
    }
  }

  return (
    <section className="bg-theme-surface border border-theme-surface-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Waypoints className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-theme-text">
          {t('config.vpnLinksTitle', 'VPN por link (multi-link)')}
        </h3>
      </div>
      <p className="text-xs text-theme-text-muted mb-3">
        {t('config.vpnLinksSubtitle', 'Um túnel VPN auxiliar por link WAN (A/B/C). Handshake e ping de cada túnel indicam a qualidade do link correspondente. Aplicado no roteador no próximo bootstrap.')}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-theme-text-muted py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('config.loading')}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((link) => {
            const alive = isVpnHandshakeFresh(link.lastHandshake)
            return (
              <div
                key={link.id}
                className={`flex items-center justify-between gap-3 p-2 border rounded ${link.enabled ? 'bg-theme-bg border-theme-border' : 'bg-theme-bg border-theme-border opacity-60'}`}
              >
                <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded text-[11px] font-bold ${alive ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700'}`}
                    title={alive
                      ? t('config.vpnLinkAlive', 'Túnel com handshake recente — link OK')
                      : t('config.vpnLinkDead', 'Sem handshake recente — link possivelmente caído')}
                  >
                    {link.linkRole}
                  </span>
                  {link.label && <span className="text-sm text-theme-text">{link.label}</span>}
                  <span className="text-xs font-mono text-theme-text-muted">{link.vpnIp || '—'}</span>
                  {link.mikrotikGateway && (
                    <span className="text-[10px] text-theme-text-muted border border-theme-border rounded px-1.5 py-0.5">
                      gw {link.mikrotikGateway}
                    </span>
                  )}
                  <span className="text-[10px] text-theme-text-muted">
                    {link.pingAvgTimeMs != null ? `${Math.round(link.pingAvgTimeMs)}ms` : '—'}
                    {link.pingPacketLoss != null ? ` · ${Math.round(link.pingPacketLoss)}% loss` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <label className="flex items-center gap-1.5 text-xs text-theme-text cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!link.enabled}
                      onChange={() => handleToggle(link)}
                      className="w-3.5 h-3.5 accent-theme-primary"
                    />
                    {t('common.enable', { defaultValue: 'Habilita' })}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleDelete(link)}
                    className="p-1 hover:bg-error-100 rounded"
                    aria-label={t('config.delete') || 'Remover'}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-error-500" />
                  </button>
                </div>
              </div>
            )
          })}

          {adding ? (
            <div className="p-3 bg-theme-surface border border-theme-primary/30 rounded space-y-2">
              <div className="flex gap-2">
                <select
                  className="input text-sm w-20"
                  value={form.linkRole}
                  onChange={(e) => setForm((f) => ({ ...f, linkRole: e.target.value }))}
                >
                  {freeRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input
                  type="text"
                  placeholder={t('config.vpnLinkLabelPlaceholder', 'Nome do link (ex.: Prefeitura)')}
                  className="input flex-1 text-sm"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder={t('config.vpnLinkGatewayPlaceholder', 'Gateway do link (ex.: 192.168.0.1)')}
                  className="input flex-1 text-sm font-mono"
                  value={form.mikrotikGateway}
                  onChange={(e) => setForm((f) => ({ ...f, mikrotikGateway: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
              </div>
              {error && <p className="text-xs text-error-600">{error}</p>}
              <p className="text-[11px] text-theme-text-muted">
                {t('config.vpnLinkGatewayHint', 'Sem gateway, o túnel sai pela rota default do roteador (sem pinning ao link).')}
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setAdding(false); setForm({ linkRole: 'A', label: '', mikrotikGateway: '' }); setError(null) }}
                  className="p-1.5 hover:bg-theme-primary/10 rounded"
                >
                  <X className="w-4 h-4 text-theme-text-muted" />
                </button>
                <button type="button" onClick={handleAdd} className="p-1.5 hover:bg-success-100 rounded">
                  <Check className="w-4 h-4 text-success-600" />
                </button>
              </div>
            </div>
          ) : (
            freeRoles.length > 0 && (
              <button
                type="button"
                onClick={() => { setForm((f) => ({ ...f, linkRole: freeRoles[0] })); setAdding(true) }}
                className="flex items-center gap-1.5 text-xs text-theme-primary hover:text-theme-primary/80 font-medium mt-1"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('config.vpnLinksAdd', 'Adicionar túnel de link')}
              </button>
            )
          )}
        </div>
      )}
    </section>
  )
}
